import { useEffect, useState } from "react";
import { useAuth } from "../contexts/AuthContext";
import { useCatalog } from "../contexts/CatalogContext";
import { db, itemKey } from "./db";
import { Favorites, deriveCatchupStreamId } from "./favorites";
import type { Stream } from "./types";

/**
 * Favourite state and toggle for one stream.
 *
 * Extracted from `StreamCard` so the phone channel row can offer favouriting
 * too. It was previously inlined there, which meant any second surface had to
 * either duplicate the optimistic-update logic — Dexie write, server call,
 * rollback on failure — or silently drop the capability. The row dropped it,
 * which left the Favorites empty state telling users to "tap the heart on any
 * channel" when phone channel rows had no heart at all.
 *
 * Writes locally first and reconciles with the server, so the UI responds
 * immediately on a slow connection and reverts if the call fails.
 */
export function useFavorite(stream: Stream) {
  const { user } = useAuth();
  const { activePlaylist, catalog } = useCatalog();
  const [isFavorite, setIsFavorite] = useState(false);

  useEffect(() => {
    if (!user || !activePlaylist) return;
    const k = itemKey(user.id, activePlaylist.id, stream.kind, stream.id);
    void db.favorites.get(k).then((row) => setIsFavorite(!!row));
  }, [user, activePlaylist, stream]);

  const toggle = async () => {
    if (!user || !activePlaylist) return;
    const kind = stream.kind;
    const k = itemKey(user.id, activePlaylist.id, kind, stream.id);

    if (isFavorite) {
      // Optimistic removal; restore the exact previous row if the server says no.
      const prev = await db.favorites.get(k);
      await db.favorites.delete(k);
      setIsFavorite(false);
      try {
        await Favorites.remove(activePlaylist.id, kind, stream.id);
      } catch {
        if (prev) await db.favorites.put(prev);
        setIsFavorite(true);
      }
      return;
    }

    // Catch-up is folded into live via `has_catchup` rather than stored as its
    // own kind, so the counterpart id is resolved at write time.
    const hasCatchup = kind === "live" && !!stream.tv_archive;
    const catchupStreamId = hasCatchup ? deriveCatchupStreamId(catalog, stream) : null;
    const poster = stream.logo || stream.cover || null;

    await db.favorites.put({
      key: k,
      userId: user.id,
      playlistId: activePlaylist.id,
      streamKind: kind,
      streamId: stream.id,
      title: stream.name,
      poster,
      hasCatchup,
      catchupStreamId,
      addedAt: Date.now(),
    });
    setIsFavorite(true);

    try {
      const saved = await Favorites.upsert(activePlaylist.id, {
        stream_kind: kind,
        stream_id: stream.id,
        title: stream.name,
        poster,
        has_catchup: hasCatchup,
        catchup_stream_id: catchupStreamId,
      });
      // Replace the optimistic row with the server's, for its id and sort order.
      await db.favorites.put({
        key: k,
        serverId: saved.id,
        userId: user.id,
        playlistId: activePlaylist.id,
        streamKind: kind,
        streamId: stream.id,
        title: saved.title,
        poster: saved.poster,
        hasCatchup: saved.has_catchup,
        catchupStreamId: saved.catchup_stream_id,
        sortIndex: saved.sort_index,
        addedAt: Date.parse(saved.created_at) || Date.now(),
      });
    } catch {
      await db.favorites.delete(k);
      setIsFavorite(false);
    }
  };

  return { isFavorite, toggle };
}
